// =========================================================================
// Engine Adapter (薄い境界、UI ↔ nagi-v9-engine)
// =========================================================================
// spec/closing_river_web_mvp_v2.md 「Engine Adapter」 を実装。
//
// 絶対則:
//   1. Runtime MUST NOT call generateBoard. → このファイルから generateBoard を
//      re-export しない (= controller から import パスが存在しない)。
//   2. Catalog entries are authoritative board snapshots; seed is provenance only.
//   3. catalog の shortest は solverFingerprint 一致時のみ valid。Silent は
//      catalogShortest を渡された場合のみ計算 (呼び出し側で gate 後に渡す)。
// =========================================================================

import {
  deserialize,
  applyOperation,
  judge,
  buildEdgePreviews,
  buildCleanEventLog,
  evaluateBeauty,
} from 'nagi-v9-engine';
import type {
  ApplyResult,
  BeautyEval,
  CleanEventLog,
  EdgeId,
  EdgePreview,
  GameState,
  JudgeResult,
  SerializedState,
} from 'nagi-v9-engine';

import type { PlayableBoard, TutorialBoard } from './catalog/manifest.js';

// -------------------------------------------------------------------------
// Catalog board → GameState
// -------------------------------------------------------------------------

/**
 * catalog snapshot を engine の GameState に復元。
 *
 * - serialized state には turnLimit (= N) が含まれる前提 (engine の serialize 仕様)。
 *   PlayableBoard 側に N があるので codex R5 errata: state.turnLimit !== board.N
 *   なら catalog corruption として throw する。
 * - TutorialBoard は N を持たないため、turnLimit は state 側の値をそのまま信頼。
 */
export function loadBoard(board: PlayableBoard | TutorialBoard): GameState {
  const state = deserialize(board.state);

  if ('N' in board && state.turnLimit !== board.N) {
    throw new Error(
      `engine-adapter.loadBoard: catalog N mismatch (state.turnLimit=${state.turnLimit}, board.N=${board.N}, boardId=${board.id})`,
    );
  }
  return state;
}

// -------------------------------------------------------------------------
// Preview / Operate / Judge
// -------------------------------------------------------------------------

/** 現在の state から、タップ可能な辺のプレビュー一覧を取得 */
export function previewEdges(state: GameState): readonly EdgePreview[] {
  return buildEdgePreviews(state);
}

/**
 * 1 手分の操作。controller は戻り値の ApplyResult を蓄積して cleanLog を作る。
 * (engine の applyOperation は ApplyResult を返し、cleanEvent / selfLoopEdgeIds
 *  を含む。これを controller 側で配列に push して buildCleanLogFromResults へ)
 */
export function operate(state: GameState, edgeId: EdgeId): ApplyResult {
  return applyOperation(state, edgeId);
}

/** 勝敗判定 (turn-limit / 非連結 / 合成不能 / 勝利)。state 内 movesUsed/turnLimit 参照 */
export function checkResult(state: GameState): JudgeResult {
  return judge(state);
}

// -------------------------------------------------------------------------
// Clean log build + Beauty 評価
// -------------------------------------------------------------------------

/** controller が蓄積した ApplyResult[] を CleanEventLog に変換 */
export function buildCleanLogFromResults(
  results: readonly ApplyResult[],
): CleanEventLog {
  return buildCleanEventLog(results);
}

/**
 * Silent / Clean / Clear 判定。
 *
 * @param catalogShortest catalog の shortest (= BFS 最短手数)。
 *                        version gate で solverFingerprint 不一致なら null を渡す
 *                        ことで Silent 判定を抑制する。
 */
export function evaluateWin(
  state: GameState,
  cleanLog: CleanEventLog,
  catalogShortest: number | null,
): BeautyEval {
  return evaluateBeauty(state, cleanLog, catalogShortest);
}

// -------------------------------------------------------------------------
// 型 re-export (controller / canvas / tutorial が直接 import できるように)
// -------------------------------------------------------------------------

export type {
  ApplyResult,
  BeautyEval,
  CleanEventLog,
  EdgeId,
  EdgePreview,
  GameState,
  JudgeResult,
  SerializedState,
};

// 注意: generateBoard / runSolver はここから絶対に re-export しない (絶対則 1)。
// build-catalog script は scripts/build-catalog.ts から engine を直接 import する。
